import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ScrollAnimationDirective } from '../../directives/scroll-animation.directive';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { QuotationFormComponent } from '../quotation-form/quotation-form.component';

@Component({
  selector: 'app-consulting',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    ScrollAnimationDirective,
    TranslateModule,
    MatDialogModule
  ],
  template: `
    <section class="section consulting-section">
      <div class="container">
        <div class="content" scrollAnimation>
          <h2 class="section-title">{{ translatedSectionTitle }}</h2>
          <div class="offers-grid">
            <div class="offer-item" *ngFor="let offer of offers">
              <mat-icon>{{ offer.icon }}</mat-icon>
              <h3>{{ offer.title }}</h3>
              <p>{{ offer.description }}</p>
            </div>
          </div>

          <div class="steps-container">
            <h3 class="steps-title">{{ stepsTitle }}</h3>
            <div class="steps-grid">
              <div class="step" *ngFor="let step of steps">
                <mat-icon>{{ step.icon }}</mat-icon>
                <h4>{{ step.step }}</h4>
                <p>{{ step.description }}</p>
              </div>
            </div>
          </div>

          <div class="cta-container">
            <button mat-raised-button class="cta-button" (click)="openQuotationForm()">
              {{ ctaButtonText }}
            </button>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .section-title {
      font-size: 2.5rem;
      margin-bottom: 1.5rem;
      font-weight: 700;
      text-align: center;
      color: #300458;
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: var(--light-text);
      margin-bottom: 3rem;
      text-align: center;
      color: #000;
    }

    .consulting-section {
      background: var(--white);
      padding: 3rem 0;
    }

    .content {
      max-width: 1000px;
      margin: 0 auto;
    }

    .offers-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 2rem;
      margin: 4rem 0;
    }

    .offer-item {
      text-align: center;
      padding: 2rem;
      background: white;
      border-radius: var(--border-radius);
      box-shadow: var(--box-shadow);
      transition: transform 0.3s ease;
    }

    .offer-item:hover {
      transform: translateY(-5px);
    }

    .offer-item mat-icon {
      font-size: 2rem;
      width: 2rem;
      height: 2rem;
      margin-bottom: 1rem;
      color: #e91e63;
    }

    .offer-item h3 {
      margin-bottom: 1rem;
      color: var(--text-color);
      font-size: 1.5rem;
    }

    .offer-item p {
      color: var(--light-text);
    }

    .steps-container {
      margin: 4rem 0;
      text-align: center;
    }

    .steps-title {
      font-size: 2rem;
      margin-bottom: 2rem;
      color: var(--text-color);
    }

    .steps-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 2rem;
    }

    .step {
      padding: 2rem;
      background: white;
      border-radius: var(--border-radius);
      box-shadow: var(--box-shadow);
      transition: transform 0.3s ease;
    }

    .step:hover {
      transform: translateY(-5px);
    }

    .step mat-icon {
      font-size: 2rem;
      width: 2rem;
      height: 2rem;
      margin: 1rem 0;
      color: #e91e63;
    }

    .step h4 {
      font-size: 1.25rem;
      margin-bottom: 0.5rem;
      color: var(--text-color);
    }

    .step p {
      color: var(--light-text);
    }

    .cta-container {
      text-align: center;
      margin-top: 4rem;
    }

    .cta-button {
      padding: 1rem 3rem !important;
      font-size: 1.25rem !important;
      background: linear-gradient(135deg, #e91e63, #9c27b0) !important;
      color: white !important;
      border-radius: 50px !important;
      transition: all 0.3s ease !important;
    }

    .cta-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(233, 30, 99, 0.4) !important;
    }

    @media (max-width: 768px) {
      .offers-grid{
      grid-template-columns: repeat(2, 1fr);
      }
    .offer-item:nth-child(3) {
    grid-column: span 2; /* Makes it take full row width */
    justify-self: center; /* Centers it within the grid */
  }

      .consulting-section {
        padding: 4rem 1rem;
      }
    }
  `]
})
export class ConsultingComponent implements OnInit {
  translatedSectionTitle: string = '';
  offers: Array<{ icon: string; title: string; description: string }> = [];
  stepsTitle: string = '';
  steps: Array<{ icon: string; step: string; description: string }> = [];
  ctaButtonText: string = '';

  constructor(
    public _translate: TranslateService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // Section title
    this.translatedSectionTitle = this._translate.instant('CONSULTING.SECTION_TITLE');

    // Offers grid texts
    this.offers = [
      {
        icon: 'health_and_safety',
        title: this._translate.instant('CONSULTING.OFFERS.COMPREHENSIVE_HEALTH_CHECK.TITLE'),
        description: this._translate.instant('CONSULTING.OFFERS.COMPREHENSIVE_HEALTH_CHECK.DESCRIPTION')
      },
      {
        icon: 'analytics',
        title: this._translate.instant('CONSULTING.OFFERS.EXPERT_ANALYSIS.TITLE'),
        description: this._translate.instant('CONSULTING.OFFERS.EXPERT_ANALYSIS.DESCRIPTION')
      },
      {
        icon: 'psychology',
        title: this._translate.instant('CONSULTING.OFFERS.TAILORED_RECOMMENDATIONS.TITLE'),
        description: this._translate.instant('CONSULTING.OFFERS.TAILORED_RECOMMENDATIONS.DESCRIPTION')
      }
    ];

    // Steps texts
    this.stepsTitle = this._translate.instant('CONSULTING.STEPS_TITLE');
    this.steps = [
      {
        icon: 'checklist',
        step: this._translate.instant('CONSULTING.STEPS.STEP_1.LABEL'),
        description: this._translate.instant('CONSULTING.STEPS.STEP_1.DESCRIPTION')
      },
      {
        icon: 'find_in_page',
        step: this._translate.instant('CONSULTING.STEPS.STEP_2.LABEL'),
        description: this._translate.instant('CONSULTING.STEPS.STEP_2.DESCRIPTION')
      },
      {
        icon: 'lightbulb',
        step: this._translate.instant('CONSULTING.STEPS.STEP_3.LABEL'),
        description: this._translate.instant('CONSULTING.STEPS.STEP_3.DESCRIPTION')
      }
    ];

    // CTA button text
    this.ctaButtonText = this._translate.instant('CONSULTING.CTA_BUTTON');
  }

  /**
   * Opens the quotation form dialog when user clicks the CTA button
   */
  openQuotationForm(): void {
    this.dialog.open(QuotationFormComponent, {
      width: '600px',
      maxWidth: '95vw',
      disableClose: false
    });
  }
}
